import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';

const CategoryModal = ({ category, onClose, onSave, loading }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (category) {
      // Editing existing category
      setName(category.name || '');
      setDescription(category.description || '');
    } else {
      // Adding new category
      setName('');
      setDescription('');
    }
    setError(''); // Clear error when modal opens or category changes
  }, [category]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Category name is required.');
      return;
    }
    setError('');
    // Pass the data up to the parent component for saving
    onSave({ 
      id: category ? category.id : null, // Include id if editing
      name: name.trim(), 
      description: description.trim() 
    });
  };

  return (
    // Modal Backdrop
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-40 flex justify-center items-center p-4"
      onClick={onClose} // Close modal if backdrop is clicked
    >
      {/* Modal Content */}
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-md relative"
        onClick={e => e.stopPropagation()} // Prevent backdrop click from closing when clicking inside modal
      >
        {/* Modal Header */}
        <div className="flex justify-between items-center p-5 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {category ? 'Edit Category' : 'Add New Category'}
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
          >
            <X className="w-5 h-5" />
            <span className="sr-only">Close modal</span>
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="category-name" className="block mb-2 text-sm font-medium text-gray-900">
              Category Name <span className="text-red-600">*</span>
            </label>
            <input 
              type="text" 
              id="category-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`bg-gray-50 border ${error ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5`}
              placeholder="e.g., Office Supplies"
              required 
            />
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          </div>
          <div>
            <label htmlFor="category-description" className="block mb-2 text-sm font-medium text-gray-900">
              Description (Optional)
            </label>
            <textarea 
              id="category-description" 
              rows="3" 
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500" 
              placeholder="Enter a brief description..."
            ></textarea>
          </div>
          
          {/* Modal Footer */}
          <div className="flex items-center justify-end pt-4 border-t border-gray-200 rounded-b">
            <button 
              type="button" // Important: type="button" to prevent form submission
              onClick={onClose}
              className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-primary-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 mr-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="text-white bg-primary-600 hover:bg-primary-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                category ? 'Save Changes' : 'Add Category'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryModal;
